import { Link } from "react-router-dom";
import type { MissionProgress, WalletMissions } from "../api";
import { Card, EmptyState, Table } from "../components";
import type { TableColumn } from "../components";
import { formatDate, formatPercent, formatTokens, isoDateTime } from "../format";
import { useCardEntrance, useGaugeFill } from "../motion";
import { ROUTES } from "../shell";
import { bucketStyle } from "../theme";
import styles from "./Dashboard.module.css";

/**
 * Jauge d'avancement d'une mission.
 *
 * Teintée du seau « consommé », parce que c'est bien lui qui avance : l'œil
 * retrouve la couleur qu'il vient de voir sur la tuile du même nom.
 *
 * `percent` est un nombre JSON du contrat, pas un montant : il est borné entre
 * 0 et 100 et se divise donc sans précaution. Les tokens de la même ligne,
 * eux, restent des chaînes et passent par `formatTokens`.
 */
function JaugeMission({ percent }: { percent: number }) {
  const remplissage = useGaugeFill<HTMLDivElement>(percent / 100);

  return (
    <div className={styles.avancement} style={bucketStyle("consumed")}>
      <div
        className={styles.avancementPiste}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={formatPercent(percent)}
        aria-label="Avancement de la mission"
      >
        <div ref={remplissage} className={styles.avancementRemplissage} />
      </div>
      <span className={styles.avancementValeur} aria-hidden="true">
        {formatPercent(percent)}
      </span>
    </div>
  );
}

/*
 * Colonnes définies une fois, hors du composant : les recréer à chaque rendu
 * ferait de `columns` une nouvelle référence à chaque fois, pour rien.
 *
 * Pas de colonne « statut » : le tableau ne montre que les missions en cours,
 * un badge qui répète « En cours » sur chaque ligne n'apprendrait rien.
 */
const COLONNES: TableColumn<MissionProgress>[] = [
  {
    key: "mission",
    header: "Projet",
    render: (m) => (
      <div className={styles.mission}>
        <span className={styles.missionLibelle}>{m.label}</span>
        <time className={styles.missionDate} dateTime={isoDateTime(m.created_at)}>
          Ouvert le {formatDate(m.created_at)}
        </time>
      </div>
    ),
  },
  {
    key: "envelope",
    header: "Budget",
    numeric: true,
    width: "9rem",
    render: (m) => formatTokens(m.envelope_tokens),
  },
  {
    key: "consumed",
    header: "Consommé",
    numeric: true,
    width: "9rem",
    render: (m) => formatTokens(m.consumed_tokens),
  },
  {
    key: "remaining",
    header: "Reste",
    numeric: true,
    width: "9rem",
    render: (m) => formatTokens(m.remaining_tokens),
  },
  {
    key: "percent",
    header: "Avancement",
    width: "12rem",
    render: (m) => <JaugeMission percent={m.percent} />,
  },
];

const TITRE = "Projets en cours";
const SOUS_TITRE =
  "Le budget réservé pour chaque projet, ce qui est déjà consommé et ce qu'il reste.";
// La carte porte déjà un titre et un sous-titre qui décrivent ce tableau :
// afficher la légende en plus empilerait deux descriptions de la même chose.
// Elle reste dans le DOM, seulement masquée à l'œil — un lecteur d'écran qui
// tombe sur un <table> sans <caption> ne sait pas ce qu'il lit.
const LEGENDE =
  "Projets en cours du portefeuille : budget réservé, tokens consommés, reste et avancement.";

/**
 * Silhouette du tableau pendant le chargement.
 *
 * Mêmes colonnes et mêmes largeurs que le tableau réel : c'est ce qui fait que
 * l'arrivée des missions ne déplace rien à l'écran.
 */
export function DashboardMissionsSquelette() {
  return (
    <Card title={TITRE} subtitle={SOUS_TITRE} padding="none">
      <Table
        caption={<span className="lde-visually-hidden">{LEGENDE}</span>}
        columns={COLONNES}
        rows={[]}
        rowKey={() => ""}
        loading
        skeletonRows={3}
      />
    </Card>
  );
}

export type DashboardMissionsProps = {
  /** Ce que rend `api.listWalletMissions` : toutes les missions, tous statuts. */
  missions: WalletMissions;
};

/**
 * Les missions en cours et leur avancement.
 *
 * Le filtrage sur `ordered` se fait ici et non côté serveur : le contrat rend
 * toutes les missions, et le décompte des closes reste utile en pied de
 * tableau — savoir qu'il n'y a rien en cours **parce que tout est clos** n'est
 * pas la même information que n'avoir jamais rien commandé.
 */
export function DashboardMissions({ missions }: DashboardMissionsProps) {
  const toutes = missions.missions;
  const enCours = toutes.filter((m) => m.status === "ordered");
  const closes = toutes.filter((m) => m.status === "completed").length;
  const annulees = toutes.filter((m) => m.status === "cancelled").length;

  // Même geste d'arrivée que les tuiles de seau : la carte des missions fait
  // partie du même écran, elle doit bouger de la même façon.
  const entree = useCardEntrance<HTMLDivElement>({ dependencies: [toutes] });

  return (
    <div ref={entree}>
      <Card
        data-anim="card"
        title={TITRE}
        subtitle={SOUS_TITRE}
        padding="none"
        footer={
          <div className={styles.piedTableau}>
            <span>
              {enCours.length} en cours · {closes} close{closes > 1 ? "s" : ""} ·{" "}
              {annulees} annulée{annulees > 1 ? "s" : ""}
            </span>
            <Link className={styles.lienAction} to={ROUTES.commande}>
              Créer un projet
            </Link>
          </div>
        }
      >
        <Table
          caption={<span className="lde-visually-hidden">{LEGENDE}</span>}
          columns={COLONNES}
          rows={enCours}
          rowKey={(m) => m.id}
          // Tableau court, et déjà porté par l'entrée en cascade de la carte :
          // une révélation au défilement en plus n'apporterait rien, et
          // laisserait les lignes invisibles tant qu'on n'a pas fait défiler.
          // La révélation par lots est faite pour l'historique, pas pour ici.
          animateRows={false}
          empty={
            <EmptyState
              compact
              title="Aucune mission en cours."
              description={
                toutes.length === 0
                  ? "Aucune prestation n'a encore été commandée sur ce portefeuille."
                  : "Toutes les missions de ce portefeuille sont closes ou annulées."
              }
            />
          }
        />
      </Card>
    </div>
  );
}
